"use client";

import { createPortal } from "react-dom";
import { Check, ChevronUp, ChevronDown, Search, RefreshCw, TrendingUp, Sparkles, ArrowRight, ArrowLeft, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOnboardingMode, WHEEL_STAGES, stageLabel, stageIndex, type WheelOnboardingStage } from "@/hooks/use-onboarding-mode";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useState, useEffect, useCallback, useRef } from "react";

const STAGE_ICONS: Record<string, React.ElementType> = {
  review: Layers,
  "find-deals": Search,
  roll: RefreshCw,
  "covered-calls": TrendingUp,
  "suggested-actions": Sparkles,
};

const STAGE_HINTS: Record<string, string> = {
  review: "Open the highlighted wheel. See every trade in its story and what you really paid for the shares.",
  "find-deals": "Open the highlighted put. See the income and risk without checking the option chain by hand.",
  roll: "Open the highlighted position. See simple ways to give the trade more time.",
  "covered-calls": "Open the highlighted stock. See where you can sell a covered call for more income.",
  "suggested-actions": "Click Suggested Actions to see income opportunities from your portfolio.",
};

export function WheelOnboardingProgress() {
  const { isOnboardingMode, wheelStage, completedStages, advanceStage, completeOnboarding, isQuickRecap, showIntro, dismissIntro, goBackStage, isFreeUser, isSpecialUser, setShowTourGuard, switchToGexTour } = useOnboardingMode();
  const { isMobile } = useMediaQuery();
  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showContinuePulse, setShowContinuePulse] = useState(false);
  // True once the pulse target has been located & highlighted for the current stage.
  // Used to gate the Continue button: while a target is blinking, user must click it
  // before they can advance. Falls back to enabled if no target ever appears.
  const [pulseTargetFound, setPulseTargetFound] = useState(false);
  const continueBtnRef = useRef<HTMLButtonElement>(null);
  const showContinuePulseRef = useRef(showContinuePulse);
  showContinuePulseRef.current = showContinuePulse;

  useEffect(() => { setMounted(true); }, []);

  const STAGE_HIGHLIGHT_TARGETS: Record<string, string> = {
    "find-deals": "row-expand-first",
    roll: "roll-position-first",
    "covered-calls": "cc-position-first",
    "suggested-actions": "action-bar-actions-tab",
  };

  // Persistent highlight: keep pulsing until user clicks the target
  useEffect(() => {
    if (showIntro) return;
    const targetName = STAGE_HIGHLIGHT_TARGETS[wheelStage];
    if (!targetName) return;

    let observer: MutationObserver | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;
    let rafId = 0;
    let dismissed = false;
    let ringEl: HTMLDivElement | null = null;

    const getTarget = () => document.querySelector(`[data-shepherd-highlight-target="${targetName}"]`);
    // Table rows can't carry the outline pulse: the table's overflow container
    // clips its left/right edges. They get a fixed-layer ring overlay instead.
    const usesRing = (el: Element) => el.tagName === 'TR';

    const removeRing = () => {
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      if (ringEl) { ringEl.remove(); ringEl = null; }
    };

    // Glue the ring to the row's bounding box, clamped to every overflow-clipping
    // ancestor so the ring hugs the visible table area instead of being cut off.
    const positionRing = (el: Element) => {
      if (!ringEl) {
        ringEl = document.createElement('div');
        ringEl.className = 'onboarding-pulse-ring';
        document.body.appendChild(ringEl);
      }
      const pad = 4;
      const rect = el.getBoundingClientRect();
      let left = rect.left - pad;
      let top = rect.top - pad;
      let right = rect.right + pad;
      let bottom = rect.bottom + pad;
      let p: HTMLElement | null = el.parentElement;
      while (p && p !== document.body) {
        const cs = getComputedStyle(p);
        if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
          const pr = p.getBoundingClientRect();
          left = Math.max(left, pr.left);
          top = Math.max(top, pr.top);
          right = Math.min(right, pr.right);
          bottom = Math.min(bottom, pr.bottom);
        }
        p = p.parentElement;
      }
      const width = Math.max(0, right - left);
      const height = Math.max(0, bottom - top);
      ringEl.style.transform = `translate(${left}px, ${top}px)`;
      ringEl.style.width = `${width}px`;
      ringEl.style.height = `${height}px`;
      ringEl.style.opacity = width > 12 && height > 12 ? '1' : '0';
    };

    const trackRing = () => {
      if (dismissed || showContinuePulseRef.current) { removeRing(); return; }
      const el = getTarget();
      if (el) positionRing(el);
      rafId = requestAnimationFrame(trackRing);
    };

    const applyPulse = () => {
      if (dismissed || showContinuePulseRef.current) return;
      const el = getTarget();
      document.querySelectorAll('.onboarding-pulse, .onboarding-pulse-row').forEach(old => {
        if (old !== el) old.classList.remove('onboarding-pulse', 'onboarding-pulse-row');
      });
      if (!el) return;
      if (usesRing(el)) {
        if (!el.classList.contains('onboarding-pulse-row')) {
          el.classList.add('onboarding-pulse-row');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setPulseTargetFound(true);
        }
        if (!rafId) rafId = requestAnimationFrame(trackRing);
      } else if (!el.classList.contains('onboarding-pulse')) {
        el.classList.add('onboarding-pulse');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setPulseTargetFound(true);
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const pulseEl = getTarget();
      if (pulseEl && (pulseEl === target || pulseEl.contains(target))) {
        dismissed = true;
        pulseEl.classList.remove('onboarding-pulse', 'onboarding-pulse-row');
        removeRing();
        setShowContinuePulse(true);
      }
    };

    // Poll for the element (it may not exist yet while data loads)
    interval = setInterval(applyPulse, 1000);
    // Also watch for DOM changes that might remove the class
    observer = new MutationObserver(applyPulse);
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', handleClick, true);

    return () => {
      dismissed = true;
      if (interval) clearInterval(interval);
      if (observer) observer.disconnect();
      document.removeEventListener('click', handleClick, true);
      removeRing();
      document.querySelectorAll('.onboarding-pulse, .onboarding-pulse-row').forEach(el => el.classList.remove('onboarding-pulse', 'onboarding-pulse-row'));
    };
  }, [wheelStage, showIntro]);

  useEffect(() => {
    setShowContinuePulse(false);
    setPulseTargetFound(false);
  }, [wheelStage]);

  // On the Active Wheels step there's no single DOM target to pulse; instead the
  // Review tab fires this event once the user has explored a wheel, which flashes
  // the "Find Deals" Continue button so they know the next move.
  useEffect(() => {
    const onExplored = () => setShowContinuePulse(true);
    window.addEventListener("qw-onboarding-wheel-explored", onExplored);
    return () => window.removeEventListener("qw-onboarding-wheel-explored", onExplored);
  }, []);

  // Once the Continue ("next step") action becomes available, let the tab bar
  // unlock the upcoming tab so the user can jump straight to it.
  useEffect(() => {
    if (showContinuePulse) {
      window.dispatchEvent(new CustomEvent("qw-onboarding-next-available", { detail: { stage: wheelStage } }));
    }
  }, [showContinuePulse, wheelStage]);

  if (!isOnboardingMode || !mounted) return null;

  // --- Intro modal ---
  if (showIntro) {
    const introPanel = (
      <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="w-full max-w-md rounded-lg overflow-hidden shadow-xl animate-in fade-in zoom-in-95 duration-200">
          <div className="h-1.5 bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
          <div className="border border-t-0 border-border/50 bg-background rounded-b-lg">
            <div className="p-5 sm:p-8 space-y-5">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold tracking-tight">
                  See how QuantWheel runs the Wheel
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  This quick tour shows the four jobs a Wheel trader repeats and where QuantWheel saves time.
                </p>
              </div>

              <div className="space-y-2.5">
                {[
                  { icon: Layers, label: "Active Wheels", desc: "See each put, assignment, and covered call in one story" },
                  { icon: Search, label: "Find Deals", desc: "Find a put to sell without checking chains by hand" },
                  { icon: RefreshCw, label: "Roll", desc: "Spot trades that need attention and compare better rolls" },
                  { icon: TrendingUp, label: "Covered Calls", desc: "Find shares that are sitting without a covered call" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-lime-400/10 text-lime-500">
                      <item.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                size="lg"
                className="w-full h-11 bg-gradient-to-r from-lime-400 to-green-400 text-zinc-900 hover:from-lime-500 hover:to-green-500 shadow-sm font-semibold"
                onClick={dismissIntro}
              >
                Start Tour
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
    return typeof document !== "undefined" ? createPortal(introPanel, document.body) : null;
  }

  // --- Progress panel ---
  const currentIdx = stageIndex(wheelStage);
  const nextStageLabel = currentIdx < WHEEL_STAGES.length - 1 ? stageLabel(WHEEL_STAGES[currentIdx + 1]) : null;

  const panel = (
    <div
      className="fixed z-[10001] bottom-0 left-0 right-0 sm:bottom-4 sm:left-4 sm:right-auto sm:w-72 sm:rounded-lg rounded-t-xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
      role="complementary"
      aria-label="Onboarding progress"
    >
      <div className="h-1 bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
      <div className="sm:rounded-b-lg border border-t-0 border-border/50 bg-background shadow-lg">
        {/* Collapsible header -- always visible */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left"
          aria-expanded={!collapsed}
        >
          <span className="text-sm font-semibold truncate">
            {isQuickRecap ? "Quick Tour" : "Getting Started"} &mdash; {currentIdx + 1}/{WHEEL_STAGES.length}
            {(() => { const t = ["~2m", "~1:30", "~1m", "~30s"]; return t[currentIdx] ? <span className="text-[10px] font-normal text-muted-foreground/60 ml-1.5">{t[currentIdx]}</span> : null; })()}
          </span>
          {collapsed ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
        </button>

        {!collapsed && (
          <div className="px-4 pb-4 space-y-3 border-t border-border/30 pt-3">
            <div className="relative">
              {WHEEL_STAGES.map((stage, i) => {
                const done = completedStages.includes(stage);
                const current = stage === wheelStage;
                const Icon = STAGE_ICONS[stage] || Search;
                const isLast = i === WHEEL_STAGES.length - 1;

                return (
                  <div key={stage} className="flex items-start gap-3 relative">
                    {!isLast && (
                      <div className={`absolute left-[13px] top-[30px] w-0.5 h-[calc(100%-20px)] ${done ? "bg-lime-400/40" : "bg-border"}`} />
                    )}
                    <div className={`
                      relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all
                      ${done ? "bg-lime-400/20 text-lime-500" : current ? "bg-foreground text-background shadow-sm shadow-lime-400/30" : "bg-muted text-muted-foreground"}
                    `}>
                      {done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                    </div>
                    <div className={`pb-3 ${isLast ? "pb-0" : ""}`}>
                      <p className={`text-sm leading-tight ${current ? "font-semibold text-foreground" : done ? "text-muted-foreground" : "text-muted-foreground/60"}`}>
                        {i + 1}. {stageLabel(stage)}
                      </p>
                      {current && (
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                          {STAGE_HINTS[stage]}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-2">
              {currentIdx > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="shrink-0 text-muted-foreground"
                  onClick={() => { setShowContinuePulse(false); goBackStage(); }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </Button>
              )}
              {(() => {
                const waitingForTargetClick = pulseTargetFound && !showContinuePulse;
                return (
                  <Button
                    ref={continueBtnRef}
                    size="sm"
                    disabled={waitingForTargetClick}
                    title={waitingForTargetClick ? "Click the highlighted item first to continue" : undefined}
                    className={`flex-1 bg-gradient-to-r from-lime-400 to-green-400 text-zinc-900 hover:from-lime-500 hover:to-green-500 shadow-sm ${showContinuePulse ? "onboarding-pulse-btn" : ""} disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none`}
                    onClick={() => { setShowContinuePulse(false); advanceStage(); }}
                  >
                    {currentIdx >= WHEEL_STAGES.length - 1
                      ? "Complete Tour"
                      : nextStageLabel || "Continue"}
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </Button>
                );
              })()}
            </div>
            <button
              type="button"
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors pt-1"
              onClick={switchToGexTour}
            >
              Looking for GEX? <span className="underline underline-offset-2">Switch to the GEX tour</span>
            </button>
            <button
              type="button"
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => {
                if (isFreeUser && !isSpecialUser) setShowTourGuard(true);
                else completeOnboarding();
              }}
            >
              Skip tour &mdash; I&apos;ll explore on my own
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(panel, document.body) : null;
}
